
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Application } from "@/types/database.types";
import { Loader2, FileEdit, FileCheck, Trash2, Clock, Eye } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useApplications } from "@/hooks/use-applications";

interface UnifiedApplicationsProps {
  onApplicationDeleted?: () => void;
}

export const UnifiedApplications = ({ onApplicationDeleted }: UnifiedApplicationsProps) => {
  const { applications, isLoading, refetch, startNewApplication } = useApplications();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAppId, setSelectedAppId] = useState<string | null>(null);
  
  // Refresh data when coming from an application submission
  useEffect(() => {
    const submissionFlag = sessionStorage.getItem('applicationSubmitted');
    const submittedAppId = sessionStorage.getItem('submittedApplicationId');
    
    if (submissionFlag && submittedAppId) {
      console.log('Refreshing data after application submission:', submittedAppId);
      refetch();
      sessionStorage.removeItem('applicationSubmitted');
      sessionStorage.removeItem('submittedApplicationId');
    }
  }, [refetch]);
  
  const handleDeleteClick = (appId: string) => {
    setSelectedAppId(appId);
    setDeleteDialogOpen(true);
  };
  
  const handleDeleteConfirm = async () => {
    if (!selectedAppId) return;
    
    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from('applications')
        .delete()
        .eq('id', selectedAppId);
        
      if (error) throw error;
      
      toast({
        title: "Application deleted",
        description: "Your application has been permanently deleted.",
      });
      
      setDeleteDialogOpen(false);
      onApplicationDeleted?.();
      refetch();
    } catch (error: any) {
      console.error('Error deleting application:', error);
      toast({
        title: "Error",
        description: "Failed to delete application. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };
  
  const getDisplayProgress = (application: Application): number => {
    if (application.status === 'submitted') {
      return 100;
    }
    return application.progress || 0;
  };
  
  const draftApplications = applications.filter(app => app.status !== 'submitted');
  const submittedApplications = applications.filter(app => app.status === 'submitted');
  const totalApplications = applications.length;
  
  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </CardContent>
      </Card>
    );
  }
  
  if (!applications || applications.length === 0) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center py-8">
            <p className="text-muted-foreground mb-4">No applications found</p>
            <Button 
              onClick={() => startNewApplication()}
              className="bg-primary text-white"
            >
              Start New Application
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  // Single application detailed view
  if (totalApplications === 1) {
    const application = applications[0];
    const progress = getDisplayProgress(application);
    const status = application.status || 'draft';
    const isSubmitted = status === 'submitted';
    
    return (
      <>
        <Card className="border">
          <CardContent className="pt-6">
            <h3 className="text-2xl font-bold mb-4">Track your PEO application</h3>
            
            <div className="space-y-6">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <p className="text-sm font-medium">Overall Progress</p>
                  <span className="text-sm font-medium text-primary">{progress}%</span>
                </div>
                <div className="h-2.5 w-full bg-slate-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary transition-all duration-500 ease-in-out" 
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </div>
              
              <div className="p-4 rounded-lg bg-slate-100 space-y-2">
                <div className="flex items-center gap-2">
                  {isSubmitted ? (
                    <Clock className="h-5 w-5 text-gray-600" />
                  ) : (
                    <FileEdit className="h-5 w-5 text-gray-600" />
                  )}
                  <h3 className="font-medium">
                    {isSubmitted ? 'Under Review' : 'Draft'}
                  </h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  {isSubmitted 
                    ? 'Your application is being reviewed' 
                    : 'Continue completing your application'
                  }
                </p>
                
                <div className="mt-3 space-y-1">
                  <p className="text-sm font-medium">
                    {application.company_legal_name || "Untitled Application"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {application.created_at && (
                      <>
                        {isSubmitted ? 'Submitted' : 'Created'} {formatDistanceToNow(new Date(application.updated_at || application.created_at), { addSuffix: true })}
                      </>
                    )}
                  </p>
                </div>
              </div>
              
              <div className="flex justify-between items-center pt-2">
                <div>
                  <p className="text-sm font-bold">Next Step</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const targetUrl = isSubmitted ? '/quotes' : `/application?id=${application.id}`;
                      navigate(targetUrl);
                    }}
                    className="gap-2"
                  >
                    {isSubmitted ? (
                      <>
                        <Eye className="h-4 w-4" />
                        View Quotes
                      </>
                    ) : (
                      <>
                        <FileEdit className="h-4 w-4" />
                        Continue Application
                      </>
                    )}
                  </Button>
                  
                  <Button
                    variant="destructive"
                    size="icon"
                    onClick={() => handleDeleteClick(application.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete this application. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                className="bg-destructive hover:bg-destructive/90"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  "Delete"
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  }
  
  // Multiple applications list view
  return (
    <>
      <div className="space-y-6">
        {draftApplications.length > 0 && (
          <div className="space-y-4">
            {draftApplications.map((app) => (
              <Card key={app.id} className="overflow-hidden">
                <CardContent className="p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="font-medium">
                        {app.company_legal_name || "Untitled Application"}
                      </h3>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-sm text-muted-foreground">
                          Status: <span className="text-amber-500 font-medium">
                            draft
                          </span>
                        </span>
                        <span className="text-sm text-muted-foreground">
                          Progress: {getDisplayProgress(app)}%
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {app.created_at && (
                          <>Created {formatDistanceToNow(new Date(app.created_at), { addSuffix: true })}</>
                        )}
                      </p>
                    </div>
                    
                    <div className="h-2.5 w-24 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary transition-all duration-500 ease-in-out" 
                        style={{ width: `${getDisplayProgress(app)}%` }}
                      ></div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          navigate(`/application?id=${app.id}`);
                        }}
                      >
                        <FileEdit className="h-4 w-4 mr-2" />
                        Continue
                      </Button>
                      
                      <Button
                        variant="destructive"
                        size="icon"
                        onClick={() => handleDeleteClick(app.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        
        {submittedApplications.length > 0 && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Submitted Applications</h3>
            {submittedApplications.map((app) => (
              <Card key={app.id} className="overflow-hidden border-primary/20">
                <CardContent className="p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="font-medium">
                        {app.company_legal_name || "Untitled Application"}
                      </h3>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-sm text-muted-foreground">
                          Status: <span className="text-primary font-medium">
                            submitted
                          </span>
                        </span>
                        <span className="text-sm text-muted-foreground">
                          Progress: {getDisplayProgress(app)}%
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {app.created_at && (
                          <>Submitted {formatDistanceToNow(new Date(app.updated_at || app.created_at), { addSuffix: true })}</>
                        )}
                      </p>
                    </div>
                    
                    <div className="h-2.5 w-24 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary transition-all duration-500 ease-in-out" 
                        style={{ width: `${getDisplayProgress(app)}%` }}
                      ></div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          navigate(`/application?id=${app.id}`);
                        }}
                      >
                        <FileCheck className="h-4 w-4 mr-2" />
                        View
                      </Button>
                      
                      <Button
                        variant="destructive"
                        size="icon"
                        onClick={() => handleDeleteClick(app.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this application. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
